import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessagesList from "../MessagesList";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";

import html2pdf from "html2pdf.js";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  previewToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    background: "#f5f7fa",
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    border: "1px solid #e5e7eb",
  },
  previewSummary: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 500,
  },
  previewButtons: {
    display: "flex",
    gap: 8,
  },
  messagesPreview: {
    maxHeight: "55vh",
    overflowY: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: theme.spacing(1),
    background: "#fafbfc",
  },
  previewItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    padding: "2px 4px",
    borderRadius: 6,
    transition: "background 0.15s",
    "&:hover": { background: "#eef2f6" },
  },
  previewItemUnchecked: {
    opacity: 0.35,
  },
  previewItemContent: {
    flex: 1,
    pointerEvents: "none",
    minWidth: 0,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: "#6b7280",
  },
  dateSeparator: {
    display: "flex",
    justifyContent: "center",
    padding: "4px 0",
    pointerEvents: "none",
  },
}));

const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&​amp;")
    .replace(/</g, "&​lt;")
    .replace(/>/g, "&​gt;")
    .replace(/"/g, "&​quot;")
    .replace(/'/g, "&​#39;");

const formatDateBR = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "—";
  }
};

const tryLoadImageAsDataUrl = (url, timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (!url || /nopicture/i.test(url)) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        finish(canvas.toDataURL("image/png"));
      } catch (e) {
        finish(url);
      }
    };
    img.onerror = () => finish(null);
    setTimeout(() => finish(null), timeoutMs);
    img.src = url;
  });

const isDateSeparator = (node) => {
  if (!node || node.nodeType !== 1) return false;
  const text = (node.textContent || "").trim();
  return text.length > 0 && text.length < 60 && !node.querySelector("img, video, audio, iframe");
};

const serializeAudioMessageForPdf = (node) => {
  const computedStyle = window.getComputedStyle(node);
  const isOutgoing = computedStyle.alignSelf === "flex-end";
  const timestampText = node.querySelector('[class*="timestamp"]')?.textContent || "";
  const timestamp = (timestampText.match(/\d{2}:\d{2}/) || [""])[0];
  const bubbleColor = isOutgoing ? "#dcf8c6" : "#ffffff";
  const bubbleAlign = isOutgoing ? "right" : "left";

  // Export a standalone card rather than a cloned audio player. The original
  // player markup carries layout rules that html2canvas does not reproduce.
  return `
    <div style="display: block; width: 100%; margin: 3px 0 7px; text-align: ${bubbleAlign};">
      <div style="display: inline-block; width: 286px; padding: 10px 12px 7px; box-sizing: border-box; background: ${bubbleColor}; border-radius: 8px; box-shadow: 0 1px 1px #b3b3b3; text-align: left; font-family: Arial, Helvetica, sans-serif;">
        <table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse: collapse; width: 100%;">
          <tr>
            <td style="width: 42px; vertical-align: middle;">
              <div style="width: 30px; height: 30px; border-radius: 15px; background: #2563eb; color: #ffffff; font-size: 15px; line-height: 30px; text-align: center;">&#9654;</div>
            </td>
            <td style="vertical-align: middle;">
              <div style="color: #1e3a8a; font-size: 11px; font-weight: 700; line-height: 15px;">MENSAGEM DE AUDIO</div>
              <div style="color: #60a5fa; font-size: 16px; font-weight: 700; line-height: 16px; letter-spacing: 2px;">&#9612;&#9616;&#9612;&#9616;&#9612;</div>
              <div style="color: #475569; font-size: 9px; line-height: 13px;">Audio recebido no atendimento</div>
            </td>
          </tr>
        </table>
        <div style="margin-top: 4px; color: #64748b; font-size: 9px; line-height: 11px; text-align: right;">${timestamp}</div>
      </div>
    </div>
  `;
};

const serializeMessageForPdf = (node) => {
  if (node.querySelector("audio")) {
    return serializeAudioMessageForPdf(node);
  }

  const clone = node.cloneNode(true);

  return clone.outerHTML;
};

const PDF_STYLES = `
  .ac-pdf-page { font-family: 'Helvetica', 'Arial', sans-serif; color: #1f2937; padding: 0; }
  .ac-pdf-header {
    background: linear-gradient(135deg, #02265C 0%, #0A4595 100%);
    color: #ffffff;
    padding: 18px 22px;
    border-radius: 12px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .ac-pdf-header .ac-brand { display: flex; align-items: center; }
  .ac-pdf-header .ac-brand-logo {
    width: 48px; height: 48px; border-radius: 12px;
    background: #ffffff;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 22px; font-weight: 700; color: #02265C;
    margin-right: 14px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.18);
    overflow: hidden;
  }
  .ac-pdf-header .ac-brand-logo img {
    width: 100%; height: 100%; object-fit: cover; display: block;
  }
  .ac-pdf-header .ac-brand-text h1 {
    font-size: 16px; margin: 0; font-weight: 700; letter-spacing: 0.2px;
    line-height: 1.2;
  }
  .ac-pdf-header .ac-brand-text p {
    font-size: 10.5px; margin: 3px 0 0; opacity: 0.92; font-weight: 400;
  }
  .ac-pdf-header .ac-meta { text-align: right; font-size: 10px; }
  .ac-pdf-header .ac-meta .ac-ticket-id {
    font-size: 14px; font-weight: 700; display: block; margin-bottom: 2px;
    letter-spacing: 0.4px;
  }
  .ac-pdf-info-card {
    border: 1px solid #e5e7eb; border-radius: 10px;
    padding: 10px 14px; margin-bottom: 14px; background: #f9fafb;
  }
  .ac-pdf-info-grid {
    width: 100%;
    border-collapse: collapse;
  }
  .ac-pdf-info-grid td {
    padding: 3px 8px;
    vertical-align: top;
    width: 50%;
  }
  .ac-pdf-info-grid .ac-lbl {
    color: #6b7280; font-weight: 700; text-transform: uppercase;
    font-size: 8px; letter-spacing: 0.55px; display: block;
  }
  .ac-pdf-info-grid .ac-val {
    color: #111827; font-weight: 500; font-size: 11px; margin-top: 1px;
    line-height: 1.3; word-break: break-word; display: block;
  }
  .ac-pdf-section-title {
    font-size: 11px; color: #374151; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.6px; margin: 14px 0 8px;
    padding-bottom: 5px;
    border-bottom: 2px solid #02265C;
  }
  .ac-pdf-messages-wrap {
    border: 1px solid #e5e7eb; border-radius: 10px; padding: 0; overflow: hidden;
  }
  .ac-pdf-footer {
    margin-top: 18px;
    padding-top: 9px;
    border-top: 1px solid #e5e7eb;
    text-align: center;
    font-size: 8.5px;
    color: #6b7280;
    line-height: 1.5;
  }
  .ac-pdf-footer strong { color: #02265C; font-weight: 700; }
  .ac-pdf-footer .ac-confidential {
    margin-top: 4px;
    font-size: 7.5px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #9ca3af;
  }
`;

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [previewItems, setPreviewItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");

  useEffect(() => {
    if (!open) {
      setTicketData(null);
      setCompanyLogo(null);
      setPreviewItems([]);
      setSelectedIds(new Set());
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        let t = null;
        try {
          const { data } = await api.get(`/tickets/${ticketId}`);
          t = data;
        } catch (e) {
        }
        if (cancelled) return;
        setTicketData(t);

        try {
          const usersResp = await api.get("/users/", {
            params: { pageNumber: 1 },
          });
          const list = usersResp?.data?.users || [];
          const admin = list.find(
            (u) => u?.profile === "admin" && u?.profileImage
          );
          if (admin?.profileImage) {
            const url = `${backendUrl}/public/avatar/${admin.profileImage}`;
            const dataUrl = await tryLoadImageAsDataUrl(url, 4000);
            if (!cancelled && dataUrl) {
              setCompanyLogo({ url, dataUrl });
            }
          }
        } catch (e) {
        }

        const listEl = document.getElementById("messagesList");
        if (listEl) {
          const items = [];
          Array.from(listEl.children).forEach((node, idx) => {
            if (node.nodeType !== 1) return;
            const id = `pdf-msg-${idx}`;
            const sep = isDateSeparator(node);
            items.push({
              id,
              html: serializeMessageForPdf(node),
              isSeparator: sep,
            });
          });
          if (!cancelled) {
            setPreviewItems(items);
            setSelectedIds(new Set(items.map((i) => i.id)));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, ticketId, backendUrl]);

  const selectableItems = useMemo(
    () => previewItems.filter((i) => !i.isSeparator),
    [previewItems]
  );
  const selectedCount = selectedIds.size;
  const totalSelectable = selectableItems.length;

  const toggleId = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(previewItems.map((i) => i.id)));
  }, [previewItems]);

  const clearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleGeneratePDF = useCallback(async () => {
    if (selectedCount === 0) {
      toast.warning("Selecione ao menos uma mensagem para exportar.");
      return;
    }
    setGenerating(true);

    try {
      const t = ticketData;

      const companyName = escapeHtml(
        t?.whatsapp?.name || t?.contact?.name || "—"
      );
      const contactName = escapeHtml(t?.contact?.name || "—");
      const contactNumber = escapeHtml(t?.contact?.number || "");
      const attendantName = escapeHtml(t?.user?.name || "Não atribuído");
      const queueName = escapeHtml(t?.queue?.name || "—");
      const ticketNum = escapeHtml(t?.id || ticketId);
      const openedAt = escapeHtml(formatDateBR(t?.createdAt));
      const closedAt =
        t?.status === "closed" ? escapeHtml(formatDateBR(t?.updatedAt)) : "—";
      const exporter = escapeHtml(user?.name || "—");
      const exportDate = escapeHtml(formatDateBR(new Date()));

      const logoHtml = companyLogo?.dataUrl
        ? `<img src="${escapeHtml(companyLogo.dataUrl)}" alt="logo" />`
        : `💬`;

      const selectedHtml = previewItems
        .filter((item) => selectedIds.has(item.id))
        .map((item) => item.html)
        .join("");

      const container = document.createElement("div");
      container.innerHTML = `
        <style>${PDF_STYLES}</style>
        <div class="ac-pdf-page">
          <div class="ac-pdf-header">
            <div class="ac-brand">
              <div class="ac-brand-logo">${logoHtml}</div>
              <div class="ac-brand-text">
                <h1>${companyName}</h1>
                <p>Relatório de Atendimento</p>
              </div>
            </div>
            <div class="ac-meta">
              <span class="ac-ticket-id">Ticket #${ticketNum}</span>
              <span>Exportado em ${exportDate}</span>
            </div>
          </div>

          <div class="ac-pdf-info-card">
            <table class="ac-pdf-info-grid">
              <tr>
                <td>
                  <span class="ac-lbl">Cliente</span>
                  <span class="ac-val">${contactName}${
        contactNumber ? " · " + contactNumber : ""
      }</span>
                </td>
                <td>
                  <span class="ac-lbl">Aberto em</span>
                  <span class="ac-val">${openedAt}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <span class="ac-lbl">Atendente</span>
                  <span class="ac-val">${attendantName}</span>
                </td>
                <td>
                  <span class="ac-lbl">Encerrado em</span>
                  <span class="ac-val">${closedAt}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <span class="ac-lbl">Conexão</span>
                  <span class="ac-val">${companyName}</span>
                </td>
                <td>
                  <span class="ac-lbl">Fila / Setor</span>
                  <span class="ac-val">${queueName}</span>
                </td>
              </tr>
            </table>
          </div>

          <div class="ac-pdf-section-title">
            Conversa (${selectedCount} itens)
          </div>
          <div class="ac-pdf-messages-wrap">${selectedHtml}</div>

          <div class="ac-pdf-footer">
            Exportado por <strong>${exporter}</strong> em ${exportDate}
            <div class="ac-confidential">Documento confidencial · Uso interno</div>
          </div>
        </div>
      `;

      const pdfOptions = {
        margin: [10, 10, 12, 10],
        filename: `atendimento_${ticketNum}_${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().from(container).set(pdfOptions).save();
      toast.success("PDF gerado com sucesso.");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setGenerating(false);
    }
  }, [
    selectedCount,
    previewItems,
    selectedIds,
    ticketData,
    ticketId,
    user,
    companyLogo,
    handleClose,
  ]);

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      open={open}
      PaperProps={{ style: { borderRadius: 12 } }}
    >
      <DialogTitle style={{ paddingBottom: 4 }}>
        <Typography variant="h6" style={{ fontWeight: 700, fontSize: 18 }}>
          Exportar conversa para PDF
        </Typography>
        <Typography variant="body2" style={{ color: "#6b7280", fontSize: 12 }}>
          Selecione abaixo as mensagens que deseja incluir no PDF.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ minHeight: 200 }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : previewItems.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              Nenhuma mensagem encontrada para exportar.
            </Typography>
            <Typography
              variant="caption"
              style={{ display: "block", marginTop: 6 }}
            >
              Abra o ticket primeiro e role para carregar o histórico desejado.
            </Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.previewToolbar}>
              <Typography className={classes.previewSummary}>
                {selectedCount} de {totalSelectable} itens selecionados
              </Typography>
              <Box className={classes.previewButtons}>
                <Button size="small" onClick={selectAll} disabled={generating}>
                  Selecionar todas
                </Button>
                <Button size="small" onClick={clearAll} disabled={generating}>
                  Limpar
                </Button>
              </Box>
            </Box>
            <Box className={classes.messagesPreview}>
              {previewItems.map((item) => {
                const isSelected = selectedIds.has(item.id);
                if (item.isSeparator) {
                  return (
                    <Box
                      key={item.id}
                      className={`${classes.previewItem} ${
                        !isSelected ? classes.previewItemUnchecked : ""
                      }`}
                      style={{ justifyContent: "center" }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleId(item.id)}
                        color="primary"
                        style={{ padding: 2 }}
                      />
                      <div
                        className={classes.previewItemContent}
                        style={{ flex: "none" }}
                        dangerouslySetInnerHTML={{ __html: item.html }}
                      />
                    </Box>
                  );
                }
                return (
                  <Box
                    key={item.id}
                    className={`${classes.previewItem} ${
                      !isSelected ? classes.previewItemUnchecked : ""
                    }`}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleId(item.id)}
                      color="primary"
                      style={{ padding: 2, marginTop: 2 }}
                    />
                    <div
                      className={classes.previewItemContent}
                      dangerouslySetInnerHTML={{ __html: item.html }}
                    />
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generating}>
          Cancelar
        </Button>
        <Button
          onClick={handleGeneratePDF}
          color="primary"
          variant="contained"
          disabled={
            generating ||
            loading ||
            previewItems.length === 0 ||
            selectedCount === 0
          }
          startIcon={
            generating ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          {generating
            ? "Gerando..."
            : `Gerar PDF (${selectedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
